import IRepository from "data/IRepository";
import { useInjection } from "infrastructure/ioc/ioc.react";
import {
  ContractingAction,
  setContractingConfig,
  setContractingProgress,
} from "infrastructure/store/contracting/ContractingActions";
import { Dispatch, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { ContractingDTO } from "features/contracting/dtos/ContractingDTO";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import { useHistory, useRouteMatch } from "react-router-dom";
import { useSearchParams } from "features/shared/hooks/useSearchParams";
import ContractingStepConfiguration from "features/contracting/models/ContractingStepConfiguration";
import DataQuery from "data/DataQuery";
import { Contracting_Configuration, Contracting_Open } from "data/endpoints";
import { getRouteParams } from "features/shared/helpers/routeParams";
import { trackPromise } from "react-promise-tracker";
import { ContractingConfigurationDTO } from "features/contracting/dtos/ContractingConfigurationDTO";
import { AlertType } from "features/shared/alerts/enums/alertType";
import { getUrl, redirectToUrl } from "features/shared/helpers/urlHelper";
import { useContractingDialog } from "features/contracting/hooks/useContractingDialog";
import { createDefaultContracting, hasContractingIdInQueryParams } from "features/contracting/helpers/contractingFormHelper";
import { getInvalidContractingLinkDialog } from "features/contracting/helpers/contractingLinkHelper";
import ContractingOpenResult from "features/contracting/models/ContractingOpenResult";
import UserManager from "infrastructure/oidc/UserManager";
import { useTranslation } from "react-i18next";

//#region models
export interface ContractingInitHookProps {
  stepsConfig: ContractingStepConfiguration[];
  routePrefix: string;
}

export interface ContractingInitHookResult {
  afterInit: boolean;
}
//#endregion models

export const useContractingInit = ({
  stepsConfig,
  routePrefix
}: 
ContractingInitHookProps): ContractingInitHookResult => {
  //#region translations
  const { i18n } = useTranslation();
  //#endregion translations

  //#region DI
  const contractingRepository = useInjection<IRepository<ContractingOpenResult>>(
    IocTypes.Repository
  );
  const contractingConfigRepository = useInjection<
    IRepository<ContractingConfigurationDTO>
  >(IocTypes.Repository);
  //#endregion DI

  //#region router
  const match = useRouteMatch();
  const queryParams = useSearchParams();
  const history = useHistory();
  //#endregion router

  //#region state
  const [afterInit, setAfterInit] = useState(false);
  //#endregion state

  //#region info dialog
  const { openInfoDialog } = useContractingDialog();
  //#endregion info dialog

  //#region store
  //  const contractingState = useSelector<IAppState, ContractingState>((store) => store.contracting);
  // dispatches
  const contractingDispatch = useDispatch<Dispatch<ContractingAction>>();
  //#endregion store

  //#endregion side-effects
  // init registration
  useEffect(() => {
    executeContractingInit();
    history.push(routePrefix); // redirect all to root page (correct step is set to contractingProgress.actualStepRoute)
  }, []);

  // re-init configuration after language change
  useEffect(() => {
    if (afterInit) {
      fetchAndDispatchContractingConfig();  
    }
  }, [i18n.language]);
  //#endregion side-effects

  //#region helpers
  const executeContractingInit = async () => {
    const sucessInit = await fetchAndDispatchContractingConfig();

    if (sucessInit) {
      if (hasContractingIdInQueryParams(queryParams)) {
        // fetch contracting
        const isEditable = await fetchAndDispatchContracting();
        if (isEditable) {
          // contracting is initialized and editable
          setAfterInit(true);
        }
        // if not editable, afterInit stays false → form is never rendered
      } else {
        // default contracting
        await dispatchDefaultContracting();
        // contracting is initialized
        setAfterInit(true);
      }
    }
  };

  const dispatchDefaultContracting = () => {
    const progress: ContractingDTO = createDefaultContracting(stepsConfig, { routePrefix: routePrefix });
    contractingDispatch(setContractingProgress(progress));
  };

  const fetchAndDispatchContracting = async (): Promise<boolean> => {
    const contratingId = queryParams.get("contractingId");
    const query: DataQuery = {
      url: Contracting_Open,
      routeParams: getRouteParams("contractingId", contratingId!),
    };

    try {
      const contracting = await trackPromise(
        contractingRepository.getOne(query)
      );

      if (contracting.isEditable && contracting.contractingData) {
        contractingDispatch(setContractingProgress(contracting.contractingData));
        return true;
      } else {
        // Contracting exists but is not editable - show info dialog
        const newContractingUrl = getUrl(match.path.substring(1));
        const dialogConfig = getInvalidContractingLinkDialog(
          contracting.contractingStateCode,
          newContractingUrl
        );
        openInfoDialog(dialogConfig.titleKey, dialogConfig.props);
        return false;
      }
    } catch (error) {
      openInfoDialog(
        "notFoundTitle",
        {
          type: AlertType.Warning,
          message: "notFoundMessage",
          primaryBtn: {
            text: "newContracting",
            action: () => redirectToUrl(getUrl(match.path.substring(1)))
          },
          secondaryBtn: {
            text: "login",
            action: () => UserManager.signinRedirect()
          }
        }
      );
      return false;
    }
  };

  const fetchAndDispatchContractingConfig = async (): Promise<boolean> => {
    let isSuccess = false;

    const query: DataQuery = {
      url: Contracting_Configuration,
    };

    try {
      const config = await trackPromise(
        contractingConfigRepository.getOne(query)
      );
      
      contractingDispatch(setContractingConfig(config));
      isSuccess = true;
    } catch (error) {
      openInfoDialog(
        "initErrorTitle",
        {
          type: AlertType.Danger,
          message: "initErrorMessage",
          primaryBtn: {
            text: "reIniting",
            action: () => redirectToUrl(getUrl(match.path.substring(1)))
          }
        }
      );
    }

    return isSuccess;
  };
  //#endregion helpers

  //#region return
  return {
    afterInit: afterInit
  }
  //#endregion return
};
